import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import type { GridColDef, GridRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import CircularProgress from './CircularProgress';
import GridInfoFooter from './GridInfoFooter';

interface SelectableCreativesGridProps<TRow extends GridValidRowModel & { id: string }> {
  /** Rows currently visible (after any "selected only" / name filters). */
  rows: TRow[];
  columns: GridColDef<TRow>[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Max selectable rows; unselected rows become unselectable once reached. */
  limit?: number;
  isLoading: boolean;
  /** Total rows before filtering, shown in the footer and used as the "data ready" check. */
  totalCount?: number;
  loadingTitle?: string;
  /** Changing this value re-arms the mount guard (e.g. when the active pack changes). */
  mountGuardKey?: unknown;
  footer?: ReactNode;
}

const SelectableCreativesGrid = <TRow extends GridValidRowModel & { id: string }>({
  rows,
  columns,
  selectedIds,
  onSelectionChange,
  limit,
  isLoading,
  totalCount,
  loadingTitle = 'Creatives are loading...',
  mountGuardKey,
  footer,
}: SelectableCreativesGridProps<TRow>) => {
  const total = totalCount ?? rows.length;
  const isLimitReached = limit !== undefined && selectedIds.length >= limit;

  const rowSelectionModel = useMemo(() => {
    const availableIds = new Set(rows.map((row) => row.id));
    return selectedIds.filter((id) => availableIds.has(id));
  }, [rows, selectedIds]);

  const isRowSelectable = useCallback(
    (params: { row: TRow }) => !isLimitReached || selectedIds.includes(params.row.id),
    [isLimitReached, selectedIds],
  );

  const hasSyncedGridSelectionRef = useRef(false);

  useEffect(() => {
    hasSyncedGridSelectionRef.current = false;
  }, [mountGuardKey]);

  const handleSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) => {
      if (isLoading || !total) return;

      const nextIds = (limit !== undefined ? model.slice(0, limit) : model).map(String);

      // DataGrid fires an empty model on mount before it applies the
      // controlled selection. Skip that first empty event so a restored
      // selection is not cleared.
      if (!hasSyncedGridSelectionRef.current) {
        hasSyncedGridSelectionRef.current = true;
        if (nextIds.length === 0 && selectedIds.length > 0) {
          return;
        }
      }

      onSelectionChange(nextIds);
    },
    [isLoading, total, limit, selectedIds, onSelectionChange],
  );

  const footerComponent = useMemo(
    // eslint-disable-next-line react/display-name
    () => () => {
      if (footer !== undefined) return <>{footer}</>;
      if (!total) return null;

      return (
        <GridInfoFooter gap={0.5} showIcon={isLimitReached}>
          <Typography variant="body2">{total} found /</Typography>
          <Typography variant="body2">
            {isLimitReached ? `Up to ${limit} creatives can be selected` : `${selectedIds.length} selected`}
          </Typography>
        </GridInfoFooter>
      );
    },
    [footer, total, isLimitReached, limit, selectedIds.length],
  );

  return (
    <Box sx={{ flex: 1, height: '100%', width: '100%', minHeight: 400, position: 'relative' }}>
      {isLoading && !rows.length ? (
        <CircularProgress title={loadingTitle} />
      ) : (
        <Box style={{ position: 'absolute', left: 0, top: 0, overflow: 'auto', height: '100%', width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            keepNonExistentRowsSelected
            loading={isLoading}
            onRowSelectionModelChange={handleSelectionModelChange}
            hideFooterPagination
            getRowId={(row) => row.id}
            isRowSelectable={isRowSelectable}
            slots={{ footer: footerComponent }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SelectableCreativesGrid;
